/**
 * FOZZIL Billing Address Modal
 * Premium modal for editing billing address
 * Matches FozzilPayModal design with dark/light theme support
 */

import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, MapPin, Check, AlertCircle, Lock } from "lucide-react";
import { supabase } from "../supabaseClient";
import FozzilPay from "../utils/FozzilPaySDK";

const BillingAddressModal = ({ isOpen, onClose, onSuccess, user, profile, editingAddress }) => {
  const [theme, setTheme] = useState(
    window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light"
  );
  const isDark = theme === "dark";
  
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  
  // Determine if we're adding a new secondary address or editing the primary profile address
  const isAddingNew = editingAddress === null;
  const isEditingPrimary = editingAddress === 'profile';
  
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    address_line: '',
    city: '',
    state: '',
    pincode: '',
    country_region: ''
  });

  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = (e) => setTheme(e.matches ? "dark" : "light");
    mediaQuery.addEventListener("change", handler);
    return () => mediaQuery.removeEventListener("change", handler);
  }, []);

  // Pre-fill with profile data when modal opens (only for editing primary)
  useEffect(() => {
    if (isOpen) {
      if (isEditingPrimary && profile) {
        setFormData({
          first_name: profile.first_name || '',
          last_name: profile.last_name || '',
          address_line: profile.address_line || '',
          city: profile.city || '',
          state: profile.state || '',
          pincode: profile.pincode || '',
          country_region: profile.country_region || 'India'
        });
      } else if (isAddingNew) {
        // For new address, pre-fill name from profile but empty address fields
        setFormData({
          first_name: profile?.first_name || '',
          last_name: profile?.last_name || '',
          address_line: '',
          city: '',
          state: '',
          pincode: '',
          country_region: 'India'
        });
      }
      setError('');
      setSuccess(false);
    }
  }, [isOpen, profile, isEditingPrimary, isAddingNew]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const validateForm = () => {
    if (!formData.first_name.trim()) {
      setError('First name is required');
      return false;
    }
    if (!formData.last_name.trim()) {
      setError('Last name is required');
      return false;
    }
    if (!formData.address_line.trim()) {
      setError('Address is required');
      return false;
    }
    if (!formData.city.trim()) {
      setError('City is required');
      return false;
    }
    if (!formData.state.trim()) {
      setError('State is required');
      return false;
    }
    if (!formData.pincode.trim() || formData.pincode.length !== 6) {
      setError('Valid 6-digit pincode is required');
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    
    setIsLoading(true);
    setError('');

    try {
      if (isEditingPrimary) {
        // Update the primary profile address
        const { error: updateError } = await supabase
          .from('profiles')
          .update({
            first_name: formData.first_name,
            last_name: formData.last_name,
            address_line: formData.address_line,
            city: formData.city,
            state: formData.state,
            pincode: formData.pincode,
            country_region: formData.country_region || 'India'
          })
          .eq('id', user?.id);

        if (updateError) {
          setError(updateError.message);
          return;
        }
      } else {
        // Add a new secondary address via FozzilPay SDK
        const result = await FozzilPay.saveAddress({
          user_id: user?.id,
          first_name: formData.first_name,
          last_name: formData.last_name,
          address_line: formData.address_line,
          city: formData.city,
          state: formData.state,
          pincode: formData.pincode,
          country_region: formData.country_region || 'India'
        });
        
        if (!result.success) {
          setError(result.message || 'Failed to save address');
          return;
        }
      }

      setSuccess(true);
      setTimeout(() => {
        onSuccess?.(formData);
        onClose();
      }, 1500);
    } catch (err) {
      console.error('Error saving address:', err);
      setError('Failed to save address. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const indianStates = [
    'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
    'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand',
    'Karnataka', 'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur',
    'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Punjab',
    'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura',
    'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
    'Delhi', 'Jammu and Kashmir', 'Ladakh', 'Puducherry'
  ];

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 md:p-6">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/60 backdrop-blur-md"
          onClick={onClose}
        />
        
        {/* Modal */}
        <motion.div
          initial={{ opacity: 0, scale: 0.92, y: 60 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.92, y: 60 }}
          transition={{ 
            duration: 0.6,
            ease: [0.16, 1, 0.3, 1] // Premium Apple-like curve
          }}
          className={`relative w-full max-w-[820px] rounded-[32px] overflow-hidden shadow-[0_24px_64px_rgba(0,0,0,0.5)] flex flex-col border backdrop-blur-3xl ${
            isDark ? 'bg-black text-white border-blue-500/25' : 'bg-white text-black border-blue-200'
          }`}
          style={{ height: '480px' }}
        >
          {/* Blue Glows */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-blue-600/[0.04] blur-[100px] pointer-events-none" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-blue-500/[0.03] blur-[80px] pointer-events-none" />
          {/* Header */}
          <div className={`flex items-center justify-between px-8 py-5 border-b ${isDark ? 'border-white/5' : 'border-gray-100'}`}>
            <div className="flex items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center mr-1 overflow-hidden border ${isDark ? 'bg-black border-white/10' : 'bg-gray-100 border-gray-200'}`}>
                <img 
                  src="/logoicon.png" 
                  alt="FOZZIL" 
                  className="w-full h-full object-cover"
                />
              </div>
              <span className={`text-xl font-bold tracking-tighter ${isDark ? 'text-white' : 'text-black'}`}>Payment & Shipping</span>
            </div>
            <button
              onClick={onClose}
              className={`px-5 py-1.5 rounded-[32px] text-sm font-semibold transition-all border ${
                isDark 
                  ? 'bg-white/5 text-white hover:bg-white/10 border-white/10' 
                  : 'bg-gray-100 text-black hover:bg-gray-200 border-gray-200'
              }`}
            >
              Close
            </button>
          </div>

          {/* Content (Overflow Scrollable) */}
          <div className="flex-1 overflow-y-auto px-6 py-3 custom-scrollbar">
            {/* Icon */}
            <div className="flex justify-center mb-2">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                isDark ? 'bg-[#065DC0]/20' : 'bg-[#065DC0]/10'
              }`}>
                <MapPin className={`w-5 h-5 ${isDark ? 'text-[#065DC0]' : 'text-[#065DC0]'}`} />
              </div>
            </div>

            {/* Title */}
            <h2 className="text-lg font-bold text-center mb-1">{isAddingNew ? 'Add New Address' : 'Billing Address'}</h2>
            <p className={`text-center text-xs mb-3 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              This address will be used for your billing and invoices.
            </p>

            {/* Form */}
            <div className="space-y-2">
              {/* Name Row */}
              <div className="grid grid-cols-2 gap-2">
                <div className={`rounded-xl px-3 py-2 border ${isDark ? 'bg-white/[0.03] border-white/5' : 'bg-gray-50/50 border-gray-100'}`}>
                  <label className={`text-[10px] font-bold uppercase tracking-wider block ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    First Name
                  </label>
                  <input
                    type="text"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleChange}
                    placeholder="John"
                    className={`w-full bg-transparent outline-none text-sm ${isDark ? 'placeholder-gray-600' : 'placeholder-gray-400'}`}
                  />
                </div>
                <div className={`rounded-xl px-3 py-2 border ${isDark ? 'bg-white/[0.03] border-white/5' : 'bg-gray-50/50 border-gray-100'}`}>
                  <label className={`text-[10px] font-bold uppercase tracking-wider block ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    Last Name
                  </label>
                  <input
                    type="text"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleChange}
                    placeholder="Doe"
                    className={`w-full bg-transparent outline-none text-sm ${isDark ? 'placeholder-gray-600' : 'placeholder-gray-400'}`}
                  />
                </div>
              </div>

              {/* Address */}
              <div className={`rounded-xl px-3 py-2 border ${isDark ? 'bg-white/[0.03] border-white/5' : 'bg-gray-50/50 border-gray-100'}`}>
                <label className={`text-[10px] font-bold uppercase tracking-wider block ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                  Street Address
                </label>
                <input
                  type="text"
                  name="address_line"
                  value={formData.address_line}
                  onChange={handleChange}
                  placeholder="123 Main Street, Apt 4B"
                  className={`w-full bg-transparent outline-none text-sm ${isDark ? 'placeholder-gray-600' : 'placeholder-gray-400'}`}
                />
              </div>

              {/* City & State */}
              <div className="grid grid-cols-2 gap-2">
                <div className={`rounded-xl px-3 py-2 border ${isDark ? 'bg-white/[0.03] border-white/5' : 'bg-gray-50/50 border-gray-100'}`}>
                  <label className={`text-[10px] font-bold uppercase tracking-wider block ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    City
                  </label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    placeholder="Mumbai"
                    className={`w-full bg-transparent outline-none text-sm ${isDark ? 'placeholder-gray-600' : 'placeholder-gray-400'}`}
                  />
                </div>
                <div className={`rounded-xl px-3 py-2 border ${isDark ? 'bg-white/[0.03] border-white/5' : 'bg-gray-50/50 border-gray-100'}`}>
                  <label className={`text-[10px] font-bold uppercase tracking-wider block ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    State
                  </label>
                  <select
                    name="state"
                    value={formData.state}
                    onChange={handleChange}
                    className={`w-full bg-transparent outline-none text-sm ${isDark ? 'text-white' : 'text-black'}`}
                  >
                    <option value="">Select</option>
                    {indianStates.map(state => (
                      <option key={state} value={state} className="text-black">{state}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Pincode & Country */}
              <div className="grid grid-cols-2 gap-2">
                <div className={`rounded-xl px-3 py-2 border ${isDark ? 'bg-white/[0.03] border-white/5' : 'bg-gray-50/50 border-gray-100'}`}>
                  <label className={`text-[10px] font-bold uppercase tracking-wider block ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    Pincode
                  </label>
                  <input
                    type="text"
                    name="pincode"
                    value={formData.pincode}
                    onChange={handleChange}
                    maxLength={6}
                    placeholder="400001"
                    className={`w-full bg-transparent outline-none text-sm font-mono ${isDark ? 'placeholder-gray-600' : 'placeholder-gray-400'}`}
                  />
                </div>
                <div className={`rounded-xl px-3 py-2 border ${isDark ? 'bg-white/[0.03] border-white/5' : 'bg-gray-50/50 border-gray-100'}`}>
                  <label className={`text-[10px] font-bold uppercase tracking-wider block ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    Country
                  </label>
                  <input
                    type="text"
                    name="country_region"
                    value={formData.country_region}
                    onChange={handleChange}
                    placeholder="India"
                    className={`w-full bg-transparent outline-none text-sm ${isDark ? 'placeholder-gray-600' : 'placeholder-gray-400'}`}
                  />
                </div>
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <motion.div 
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-2 flex items-center gap-2 text-red-500 text-xs"
              >
                <AlertCircle className="w-3 h-3" />
                <span>{error}</span>
              </motion.div>
            )}

            {/* Success Message */}
            {success && (
              <motion.div 
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="mt-2 flex items-center gap-2 text-green-500 text-xs justify-center"
              >
                <Check className="w-4 h-4" />
                <span className="font-semibold">Address saved successfully!</span>
              </motion.div>
            )}

          </div>

          {/* Footer (Centered Button) */}
          <div className="px-6 pb-4 flex flex-col items-center gap-2">
            <button
              onClick={handleSubmit}
              disabled={isLoading || success}
              className={`w-[120px] py-2 rounded-full font-black uppercase tracking-widest transition-all text-[10px] shadow-lg ${
                isLoading || success
                  ? 'bg-gray-400 cursor-not-allowed text-white'
                  : isDark 
                    ? 'bg-white text-black hover:scale-105 active:scale-95' 
                    : 'bg-black text-white hover:scale-105 active:scale-95'
              }`}
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />
                  Saving...
                </span>
              ) : success ? (
                'Saved!'
              ) : (
                'Done'
              )}
            </button>

            {/* Security Badge */}
            <div className={`flex items-center justify-center gap-1.5 text-[9px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
              <Lock className="w-2.5 h-2.5 grayscale opacity-70" />
              <span className="uppercase tracking-widest font-bold opacity-70">Secured by FOZZIL</span>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default BillingAddressModal;
